import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import { ReactComponent as CloseIcon } from '../../../../../../shared/icons/close-icon.svg';
import { OptionalMessageInputRef } from '../../../../document-wizard-modal/custom-modals/document-wizard-action-modal/OptionalMessageInputRef';
import { ModalActionDescription, ModalActionTitle } from '../../../../../document-table/DocumentTableConstants';
import { Warning } from '../../../../../shared/warning';
import { DocumentLoadingSpinner } from '../../../../document-loading-spinner/DocumentLoadingSpinner';
import InviteReceivingTeamFormik from '../../../../../../forms/InviteReceivingTeamFormContainer';
import Tooltip from '../../../../../tooltip';
import { DocumentStatusEnum } from '../../../../../../shared/constants/document';
import { CounterpartyPermissionLevel, COUNTERPARTY_PERMISSION_LABELS } from '../../../../../../types/counterparty-permissions';
import styles from './GrantAccessSlideout.module.scss';

interface GrantAccessSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  isDealShared: boolean;
  hasCounterparty?: boolean;
  teamId: string;
  handler: () => void;
  isLoading: boolean;
  suggestionsAlert: { title: string; text: string } | null;
  optionalMessageInputRef: React.RefObject<HTMLTextAreaElement>;
  counterpartyPermissionLevel: CounterpartyPermissionLevel;
  onCounterpartyPermissionChange: (level: CounterpartyPermissionLevel) => void;
  documentSource?: string;
  documentStatus?: string;
  isDocumentCreator?: boolean;
}

const GrantAccessSlideout = ({
  isOpen,
  onClose,
  isDealShared,
  hasCounterparty = false,
  teamId,
  handler,
  isLoading,
  suggestionsAlert,
  optionalMessageInputRef,
  counterpartyPermissionLevel,
  onCounterpartyPermissionChange,
  documentSource,
  documentStatus,
  isDocumentCreator,
}: GrantAccessSlideoutProps) => {
  // Don't render anything if not showing
  if (!isOpen) {
    return null;
  }

  // Check if document is executed (read-only, no editing permissions should be shown)
  const isExecuted = documentStatus === DocumentStatusEnum.EXECUTED;

  // Check if we should show the invite form (when no counterparty exists)
  const shouldShowInviteForm = !isDealShared && !hasCounterparty;

  // Memoized container components for InviteReceivingTeamFormik
  const BodyContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div className={styles.slideout__form}>{children}</div>
  ));

  const ActionsContainer = React.memo(({ children }: { children?: React.ReactNode }) => (
    <div className={styles.slideout__footer}>{children}</div>
  ));

  // Use React Portal to render slideout outside current DOM tree
  return ReactDOM.createPortal(
    <>
      {/* Dark overlay backdrop */}
      {isOpen && <div className={styles.slideoutBackdrop} />}

      <div
        className={classNames(
          styles.slideoutWrapper,
          isOpen ? styles.slideoutWrapper__show : styles.slideoutWrapper__collapsed
        )}
      >
        <div className={styles.slideout}>
          {/* Sticky Header */}
          <div className={styles.slideout__header}>
            <div className={styles.slideout__title}>
              <FormattedMessage
                id="document-wizard-modal.grant-access-title"
                defaultMessage={ModalActionTitle.GRANT_ACCESS}
              />
            </div>
            <div className={styles.slideout__close} onClick={onClose}>
              <CloseIcon />
            </div>
          </div>

          {/* Scrollable Content */}
          <div className={styles.slideout__content}>
            {/* Show invitation form when no counterparty exists */}
            {shouldShowInviteForm ? (
              <InviteReceivingTeamFormik
                shouldAccessBeGranted={true}
                sendDocumentToReceiverHandler={handler}
                renderSecondaryAction={() => (
                  <Button variant="link" className="text-secondary" onClick={onClose}>
                    <FormattedMessage
                      id="lease-document-wizard-preview-grant-access.cta.cancel"
                      defaultMessage="Cancel"
                    />
                  </Button>
                )}
                bodyContainer={BodyContainer}
                actionsContainer={ActionsContainer}
                teamId={teamId}
                sendWithDocumentId={true}
                shareDocument={true}
                refreshCounterpartyData={() => {}}
              />
            ) : (
              /* Show normal grant access flow when counterparty exists */
              <div className={styles.slideout__form}>
                {/* Suggestions Warning */}
                {suggestionsAlert && (
                  <div className={styles.slideout__warning}>
                    <Warning text={suggestionsAlert.text} title={suggestionsAlert.title} />
                  </div>
                )}

                {/* Description */}
                <div className={styles.slideout__description}>
                  <FormattedMessage
                    id="lease-document-wizard-preview-grant-access.description"
                    defaultMessage={ModalActionDescription.GRANT_ACCESS}
                  />
                </div>

                {/* Counterparty Permission Dropdown - Only show for document creators with imported PDFs (non-executed) */}
                {!isExecuted && isDocumentCreator && documentSource === 'imported' && (
                  <div className={styles.slideout__permissionsSection}>
                    <div className={styles.slideout__permissionRow}>
                      <label className={styles.slideout__permissionLabel}>
                        <span>Counterparty Permissions</span>
                        <Tooltip
                          testId="counterparty-permission-tooltip-grant-access"
                          placement="top"
                        >
                          Set counterparty capabilities
                        </Tooltip>
                      </label>
                      <select
                        className={styles.slideout__permissionSelect}
                        value={counterpartyPermissionLevel}
                        onChange={(e) => onCounterpartyPermissionChange(e.target.value as CounterpartyPermissionLevel)}
                      >
                        {Object.entries(COUNTERPARTY_PERMISSION_LABELS).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Optional Message Input */}
                <div className={styles.slideout__messageSection}>
                  <OptionalMessageInputRef ref={optionalMessageInputRef} />
                </div>
              </div>
            )}
          </div>

          {/* Footer with Actions - only show when not showing invite form (invite form has its own footer) */}
          {!shouldShowInviteForm && (
            <div className={styles.slideout__footer}>
              <Button variant="link" className="text-secondary" onClick={onClose}>
                <FormattedMessage
                  id="lease-document-wizard-preview-grant-access.cta.cancel"
                  defaultMessage="Cancel"
                />
              </Button>
              <Button variant="secondary" onClick={handler} disabled={isLoading}>
                <FormattedMessage
                  id="lease-document-wizard-preview-grant-access.cta.grant-access"
                  defaultMessage="Grant Access"
                />
                {isLoading && <DocumentLoadingSpinner />}
              </Button>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default GrantAccessSlideout;
